// Platform: web — uses window.print(), document.querySelector, and @media print CSS
// Purpose: printable equipment temperature log report rendered as a letter-size paper layout (cover sheet + readings pages)
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { Box, HStack, VStack } from 'native-base';
import { flatMap, map, orderBy, startCase } from 'lodash';
import moment from 'moment';

import { Button, Text } from '@pimm/base';
import { PrinterIcon } from '../icons/printer';
import { OpsbeeWordmark } from '../icons/opsbee-wordmark';
import { convertMinutesToHoursAndMinutes, dateDiffToMinutes, formatToShortDateTime, stringToDateLocal } from '@app/utils/date-formatter';
import { EquipmentInstancesDto, LinkedInstanceChartDataDto, StoreEventsDto } from '@pimm/services/lib/store-equipment';
import { EventThresholdMode } from '@pimm/services/lib/threshold-profiles';
import { buildChartStatTiles, buildEquipmentExportRows, getViolationTriggerLabel, tempValue } from '../_helper';
import { ThresholdColors } from '../consts';
import { useGetStoreEvents } from '../hooks/get-store-events.hook';
import { AlertOctagonIcon } from '../icons/alert-octagon';
import { OutlineBadgeCheckIcon } from '../icons/outline-badge-check';
import { PrintTemperatureChart } from './print-temperature-chart';

// Style block scoped to the report-print surface.
const PRINT_STYLES = `
  .equipment-report-screen-wrap {
    background: #f3f4f6;
    padding: 24px 0;
    overflow-y: auto;
    max-height: calc(90vh - 56px);
  }
  .equipment-report-paper {
    width: 8.5in;
    height: 11in;
    margin: 0 auto;
    background: white;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.12);
    padding: 0.6in 0.55in;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .equipment-report-paper + .equipment-report-paper { margin-top: 24px; }
  .equipment-report-paper-body { flex: 1 1 auto; min-height: 0; overflow: hidden; }
  .equipment-report-page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 6px;
    margin-bottom: 10px;
    flex-shrink: 0;
  }
  .equipment-report-page-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 4px;
    margin-top: 4px;
    font-family: 'Inter', sans-serif;
    font-size: 9px;
    color: #6b7280;
    flex-shrink: 0;
  }
  .equipment-report-page-footer > *:nth-child(2) { text-align: center; }
  .equipment-report-page-footer > *:nth-child(3) { text-align: right; }

  .equipment-report-section-bar {
    background: #f1f3f5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    border-radius: 3px;
    padding: 5px 10px;
    margin-bottom: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #111827;
    letter-spacing: 0;
  }

  .equipment-report-store-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 18px;
    row-gap: 8px;
    font-family: 'Inter', sans-serif;
    margin-bottom: 2px;
  }
  .equipment-report-store-cell-label {
    font-size: 8px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 1px;
  }
  .equipment-report-store-cell-value {
    font-size: 10px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }

  .equipment-report-event-table { width: 100%; border-collapse: collapse; font-family: 'Inter', sans-serif; }
  .equipment-report-event-table th { text-align: left; font-size: 9px; letter-spacing: 0.5px; text-transform: uppercase; color: #6b7280; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
  .equipment-report-event-table td { font-size: 11px; color: #111827; padding: 6px 8px; border-bottom: 1px solid #f3f4f6; }
  .equipment-report-event-table tr:nth-child(even) td { background: #fafafa; }

  .equipment-report-readings-page-grid { display: flex; gap: 0.18in; align-items: flex-start; }
  .equipment-report-readings-col { flex: 1 1 0; min-width: 0; font-family: 'Inter', sans-serif; }
  .equipment-report-readings-col-header,
  .equipment-report-readings-col-row {
    display: grid;
    column-gap: 4px;
    align-items: baseline;
    padding: 1.5px 6px;
    line-height: 1.2;
  }
  .equipment-report-readings-col-header {
    background: #f3f4f6;
    border-bottom: 1px solid #d1d5db;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #4b5563;
  }
  .equipment-report-readings-col-row {
    border-bottom: 1px solid #f3f4f6;
    font-size: 9px;
    color: #111827;
  }
  .equipment-report-readings-col-row:nth-child(even) { background: #fafafa; }
  .equipment-report-readings-col-row .col-temp,
  .equipment-report-readings-col-row .col-door {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    font-variant-numeric: tabular-nums;
  }
  .equipment-report-readings-col-row .reading-dot-slot {
    width: 6px;
    height: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .equipment-report-readings-col-row .reading-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: block;
  }
  .equipment-report-readings-col-row .col-temp,
  .equipment-report-readings-col-header .col-temp,
  .equipment-report-readings-col-row .col-door,
  .equipment-report-readings-col-header .col-door { text-align: right; }
  .equipment-report-readings-with-door .equipment-report-readings-col-header,
  .equipment-report-readings-with-door .equipment-report-readings-col-row {
    grid-template-columns: 1.5fr 0.7fr 0.7fr;
  }
  .equipment-report-readings-no-door .equipment-report-readings-col-header,
  .equipment-report-readings-no-door .equipment-report-readings-col-row {
    grid-template-columns: 1.7fr 1fr;
  }

  @media print {
    @page { size: letter; margin: 0; }
    html, body {
      background: white !important;
      margin: 0 !important;
      height: auto !important;
      overflow: visible !important;
    }
    body > *:not(.equipment-report-print-modal) { display: none !important; }
    .equipment-report-print-modal {
      display: block !important;
      position: static !important;
      width: 100% !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      background: white !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .equipment-report-print-chrome { display: none !important; }
    .equipment-report-screen-wrap {
      padding: 0 !important;
      background: white !important;
      overflow: visible !important;
      max-height: none !important;
    }
    .equipment-report-paper {
      box-shadow: none !important;
      margin: 0 !important;
    }
    .equipment-report-paper + .equipment-report-paper {
      margin-top: 0 !important;
      page-break-before: always;
      break-before: page;
    }
  }
`;

// Readings layout constants — tuned for an 11in letter sheet.
const READINGS_ROWS_PER_COLUMN = 51;
const READINGS_COLUMNS_PER_PAGE = 3;
const READINGS_PER_PAGE = READINGS_ROWS_PER_COLUMN * READINGS_COLUMNS_PER_PAGE;

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export type ReportPrintViewProps = {
  equipmentFocus: EquipmentInstancesDto;
  // Full chart-data array (every InstanceId group, not just the temperature
  // group). The print view independently picks the Temperature-bearing
  // group for stats / chart / temp readings and the Door-bearing group
  // for door state — matching how TemperatureChart selects them.
  chartData?: LinkedInstanceChartDataDto[] | undefined;
  // Site-wide hardware sensor configs (from /Hardware endpoint via
  // useGetHardwareConfig). The print view picks the rows matching the
  // focused EquipmentId — typically one temp sensor + one door sensor.
  hardwareSensors?: any[];
  startDate: Date;
  endDate: Date;
  onClose: () => void;
  // Optional site/organization context for report header. Fields default
  // to empty string when not provided.
  siteInfo?: { name?: string; customerName?: string; address?: string; logoUrl?: string };
  storeEvents?: StoreEventsDto;
  // Username or display name shown in the report footer.
  generatedBy?: string;
};

export const ReportPrintView = ({
  equipmentFocus,
  chartData,
  hardwareSensors,
  startDate,
  endDate,
  siteInfo,
  storeEvents,
  generatedBy,
  ..._props
}: ReportPrintViewProps) => {
  const { t } = useTranslation();
  const isReady = useBoolean(false);

  const linkedInstances = (chartData ?? []) as LinkedInstanceChartDataDto[];
  const linkedTemperature = linkedInstances.find(g => !!g?.Temperature);

  const sensorName = equipmentFocus.TemperatureMetrics?.Temperature?.Description ?? '';
  const thresholds = equipmentFocus.TemperatureMetrics?.Temperature?.Thresholds;
  const stats = linkedTemperature?.Temperature?.Statistics;
  const realTimeStats = linkedTemperature?.RealTimeStatus?.Statistics;

  const eventRows = useMemo(() => {
    const eventGroups: { eventType: string; events: any[] }[] = [
      { eventType: 'temperature', events: flatMap(storeEvents?.Equipment, _ => _.TemperatureEvents ?? []) },
      { eventType: 'offline', events: flatMap(storeEvents?.Equipment, _ => _.RealTimeStatusEvents ?? []) },
      { eventType: 'door open', events: flatMap(storeEvents?.Equipment, _ => _.DoorEvents ?? []) },
      { eventType: 'low battery', events: flatMap(storeEvents?.Equipment, _ => _.BatteryEvents ?? []) },
    ];

    const rows = eventGroups.reduce<
      Array<{ type: string; severity: string; trigger: string; startTime: string; endTime: string; duration: string; startMs: number }>
    >((acc, group) => {
      const mapped = map(group.events, _event => {
        const startTime = stringToDateLocal(_event?.StartTime);
        const endTime = stringToDateLocal(_event?.EndTime);
        const trigger = getViolationTriggerLabel(group.eventType, _event?.ViolationTriggerValue, _event?.ThresholdMode === EventThresholdMode.Upper);
        const minutes = startTime && endTime ? dateDiffToMinutes(startTime, endTime) : 0;
        return {
          type: startCase(group.eventType),
          severity: _event?.Priority ?? '',
          trigger,
          startTime: formatToShortDateTime(startTime) ?? '',
          endTime: formatToShortDateTime(endTime) ?? '',
          duration: convertMinutesToHoursAndMinutes(minutes) ?? '',
          startMs: startTime ? startTime.getTime() : 0,
        };
      });
      return acc.concat(mapped);
    }, []);

    return orderBy(rows, ['startMs'], ['desc']);
  }, [storeEvents]);

  const rawRows = useMemo(() => buildEquipmentExportRows(chartData), [chartData]);

  const statTiles = buildChartStatTiles(stats, realTimeStats);

  const rangeLabel = `${moment(startDate).format('MMM D, YYYY h:mm A')} — ${moment(endDate).format('MMM D, YYYY h:mm A')}`;
  const generatedAt = moment().format('MMM D, YYYY h:mm A');

  const focusedSensors = (() => {
    const tempDesc = equipmentFocus.TemperatureMetrics?.Temperature?.Description?.toLowerCase();
    const eqId = equipmentFocus.EquipmentId;
    if (!eqId || !tempDesc) return { temperatureSensor: undefined, doorSensor: undefined };
    const rows = (hardwareSensors ?? []).filter((s: any) => s?.EquipmentId === eqId);
    const temperatureSensor = rows.find((s: any) => s?.Label?.toLowerCase() === tempDesc);
    const doorSensor = rows.find((s: any) => s !== temperatureSensor);
    return { temperatureSensor, doorSensor };
  })();

  // Temporarily reparent the modal to <body> before window.print() so the
  // browser's print engine sees the full paper stack without the modal's
  // overflow:hidden clip. Restored on afterprint.
  const handlePrint = () => {
    const node = document.querySelector('.equipment-report-print-modal');
    if (!node || !node.parentNode) {
      window.print();
      return;
    }
    const placeholder = document.createComment('print-modal-placeholder');
    node.parentNode.insertBefore(placeholder, node);
    document.body.appendChild(node);

    const restore = () => {
      window.removeEventListener('afterprint', restore);
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(node, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };
    window.addEventListener('afterprint', restore);
    window.print();
  };

  useEffect(() => {
    // For reference only
    // let animation = requestAnimationFrame(() => isReady.setTrue());
    // return () => {
    //   cancelAnimationFrame(animation);
    // };
    const timeoutRef = setTimeout(() => isReady.setTrue(), 1000);
    return () => {
      clearTimeout(timeoutRef);
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      // no-op for now; hook is here in case we want analytics later
    };
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, []);

  if (!isReady.value) {
    return (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: `@keyframes equipmentReportSpin { to { transform: rotate(360deg); } }`,
          }}
        />
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '40px 60px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: '4px solid #E5E7EB',
                borderTopColor: '#FCB816',
                borderRadius: '50%',
                animation: 'equipmentReportSpin 0.8s linear infinite',
              }}
            />
            <div style={{ color: '#4B5563', fontSize: 14, fontWeight: 500 }}>{t('store_equipment:generating_report')}</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />

      <div className="equipment-report-print-modal">
        <div
          className="equipment-report-print-chrome"
          style={{
            background: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderBottom: '1px solid #E5E7EB',
            padding: '8px 16px',
          }}
        >
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <HStack space={3} alignItems="center">
              <PrinterIcon color="gray.700" size={5} />
              <Text size="lg" fontWeight={700} color="black">
                {t('store_equipment:print_preview')}
              </Text>
            </HStack>

            <HStack space={2}>
              <Button
                size="md"
                variant="unstyled"
                borderColor="gray.300"
                bg="white"
                _text={{ color: 'gray.900' }}
                shadow={1}
                onPress={_props.onClose}
              >
                {t('store_equipment:close')}
              </Button>

              <Button size="md" shadow={0} startIcon={<PrinterIcon color="black" size={4} />} onPress={handlePrint}>
                {t('store_equipment:print')}
              </Button>
            </HStack>
          </HStack>
        </div>

        <div className="equipment-report-screen-wrap">
          {(() => {
            const hasDoor = rawRows.some(r => r['Door (1=Closed, 2=Open)'] !== '');
            const classifyTemp = (val: number): 'red' | 'yellow' | null => {
              if (!thresholds) return null;
              if (typeof thresholds.UpperRed === 'number' && val >= thresholds.UpperRed) return 'red';
              if (typeof thresholds.UpperYellow === 'number' && val >= thresholds.UpperYellow) return 'yellow';
              if (typeof thresholds.LowerRed === 'number' && val <= thresholds.LowerRed) return 'red';
              if (typeof thresholds.LowerYellow === 'number' && val <= thresholds.LowerYellow) return 'yellow';
              return null;
            };
            const displayRows = rawRows.map(row => {
              const tempNum = typeof row['Temp (°F)'] === 'number' ? (row['Temp (°F)'] as number) : null;
              const doorNum = row['Door (1=Closed, 2=Open)'];
              return {
                time: row.Timestamp,
                temp: tempNum == null ? '—' : tempNum.toFixed(1),
                tempBand: tempNum == null ? null : classifyTemp(tempNum),
                door: doorNum === '' ? '—' : doorNum === 1 ? t('store_equipment:closed') : t('store_equipment:open'),
                doorOpen: doorNum === 2,
              };
            });
            const readingsPages = chunk(displayRows, READINGS_PER_PAGE);
            const layoutClass = hasDoor ? 'equipment-report-readings-with-door' : 'equipment-report-readings-no-door';
            const timeHeader = t('store_equipment:time');
            const tempHeader = `${t('store_equipment:temp')} (°F)`;
            const doorHeader = t('store_equipment:door');
            const totalPages = 1 + readingsPages.length;

            const renderPageHeader = () => (
              <div className="equipment-report-page-header">
                <HStack alignItems="center" space={3} flex={1}>
                  <HStack alignItems="center" space={2}>
                    {!!siteInfo?.logoUrl && <img src={siteInfo.logoUrl} style={{ height: 40, objectFit: 'contain' }} />}
                    <VStack>
                      <Text size="lg" fontWeight={700} color="black" textTransform="uppercase" letterSpacing={0.3} lineHeight="xs">
                        {t('store_equipment:equipment_temperature_log')}
                      </Text>
                      <Text size="sm" color="gray.700" fontWeight={500} lineHeight="xs">
                        {sensorName}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>
                <VStack alignItems="flex-end" space={0.5}>
                  <Text size="xs" color="gray.700" fontWeight={600}>
                    {rangeLabel}
                  </Text>
                  <Text size="xs" color="gray.700" fontWeight={600}>
                    {siteInfo?.name ?? ''}
                  </Text>
                </VStack>
              </div>
            );

            const renderPageFooter = (pageNumber: number) => (
              <div className="equipment-report-page-footer">
                <span style={{ display: 'flex', alignItems: 'center', justifySelf: 'start' }}>
                  <OpsbeeWordmark height={17} />
                </span>
                <span>{`${t('store_equipment:generated_at')} ${generatedAt} · ${generatedBy ?? ''}`}</span>
                <span style={{ textAlign: 'right' }}>
                  {t('store_equipment:page')} {pageNumber} {t('store_equipment:of')} {totalPages}
                </span>
              </div>
            );

            return (
              <>
                {/* Page 1 — cover sheet: equipment info / stats / chart / events */}
                <div className="equipment-report-paper">
                  {renderPageHeader()}
                  <div className="equipment-report-paper-body">
                    {/* Equipment Information — 3-column × 2-row metadata grid */}
                    {(() => {
                      const customerName = siteInfo?.customerName ?? '—';
                      const addressStr = siteInfo?.address ?? '';
                      const temp = focusedSensors.temperatureSensor;
                      const door = focusedSensors.doorSensor;
                      const formatSensor = (s: any) => (
                        <>
                          {!!s?.AdditionalProperties?.Model && <div>{s.AdditionalProperties.Model}</div>}
                          <div>
                            {'S/N '}
                            <span style={{ fontWeight: 700 }}>{s?.Serial ?? '—'}</span>
                          </div>
                        </>
                      );
                      return (
                        <Box pb={3}>
                          <div className="equipment-report-section-bar">{t('store_equipment:equipment_information')}</div>
                          <div className="equipment-report-store-grid">
                            {/* Row 1 */}
                            <div>
                              <div className="equipment-report-store-cell-label">{t('store_equipment:customer')}</div>
                              <div className="equipment-report-store-cell-value">{customerName}</div>
                            </div>
                            <div>
                              <div className="equipment-report-store-cell-label">{t('store_equipment:store')}</div>
                              <div className="equipment-report-store-cell-value">{siteInfo?.name ?? ''}</div>
                            </div>
                            <div>
                              <div className="equipment-report-store-cell-label">{t('store_equipment:address')}</div>
                              <div className="equipment-report-store-cell-value">{addressStr || '—'}</div>
                            </div>

                            {/* Row 2 */}
                            <div>
                              <div className="equipment-report-store-cell-label">{t('store_equipment:equipment_name')}</div>
                              <div className="equipment-report-store-cell-value">{sensorName}</div>
                            </div>
                            <div>
                              <div className="equipment-report-store-cell-label">{t('store_equipment:temperature_sensor')}</div>
                              <div className="equipment-report-store-cell-value">{temp ? formatSensor(temp) : '—'}</div>
                            </div>
                            {door ? (
                              <div>
                                <div className="equipment-report-store-cell-label">{t('store_equipment:door_sensor')}</div>
                                <div className="equipment-report-store-cell-value">{formatSensor(door)}</div>
                              </div>
                            ) : (
                              <div />
                            )}
                          </div>
                        </Box>
                      );
                    })()}

                    {/* Temperature Trend section */}
                    <Box pb={4}>
                      <div className="equipment-report-section-bar">{t('store_equipment:temperature_trend')}</div>
                      {/* Threshold legend chips — mirrors live chart title-row */}
                      {!!thresholds && (
                        <HStack space={1} alignItems="center" pb={2}>
                          <HStack rounded="full" borderWidth={1} borderColor="gray.100" alignItems="center" p={1} pr={2} space={1.5}>
                            <Box rounded="full" alignItems="center" justifyContent="center" p={0.5} bgColor="error.600">
                              <AlertOctagonIcon color="white" size={3} />
                            </Box>
                            <Text size="sm" color="gray.700" fontWeight={600} lineHeight="xs">
                              {`<${tempValue(thresholds.LowerRed)}`}
                            </Text>
                          </HStack>
                          <HStack rounded="full" borderWidth={1} borderColor="gray.100" alignItems="center" p={1} pr={2} space={1.5}>
                            <Box rounded="full" bgColor="blueLight.500" alignItems="center" p={0.5}>
                              <OutlineBadgeCheckIcon color="white" size={3} />
                            </Box>
                            <Text size="sm" color="gray.700" fontWeight={600} lineHeight="xs">
                              {`${(thresholds.LowerGreen ?? 0).toFixed(1)} - ${tempValue(thresholds.UpperGreen)}`}
                            </Text>
                          </HStack>
                          <HStack rounded="full" borderWidth={1} borderColor="gray.100" alignItems="center" p={1} pr={2} space={1.5}>
                            <Box rounded="full" bgColor="error.600" alignItems="center" p={0.5}>
                              <AlertOctagonIcon color="white" size={3} />
                            </Box>
                            <Text size="sm" color="gray.700" fontWeight={600} lineHeight="xs">
                              {`>${tempValue(thresholds.UpperRed)}`}
                            </Text>
                          </HStack>
                        </HStack>
                      )}
                      <HStack space={5} alignItems="baseline" pb={3} flexWrap="wrap">
                        {statTiles.map((tile, idx) => (
                          <HStack key={idx} alignItems="baseline" space={1.5}>
                            <Text size="2xs" color="gray.500" fontWeight={600} textTransform="uppercase" letterSpacing={0.4} numberOfLines={1}>
                              {t(tile.title)}
                            </Text>
                            <Text size="xs" color="black" fontWeight={700}>
                              {tile.value}
                            </Text>
                          </HStack>
                        ))}
                      </HStack>
                      <Box borderWidth={1} borderColor="gray.200" borderRadius="md" p={2}>
                        <PrintTemperatureChart
                          startDate={startDate}
                          endDate={endDate}
                          linkedInstances={linkedInstances}
                          thresholds={thresholds}
                          width={696}
                          height={240}
                        />
                      </Box>
                    </Box>

                    {/* Event Log */}
                    <Box pb={4}>
                      <div className="equipment-report-section-bar">
                        {t('store_equipment:event_log')} ({eventRows.length})
                      </div>
                      {eventRows.length === 0 ? (
                        <Box borderWidth={1} borderColor="gray.200" borderRadius="md" px={3} py={4}>
                          <Text size="sm" color="gray.500" textAlign="center">
                            {t('store_equipment:no_events')}
                          </Text>
                        </Box>
                      ) : (
                        <table className="equipment-report-event-table">
                          <thead>
                            <tr>
                              <th>{t('store_equipment:event_type').toUpperCase()}</th>
                              <th>{t('store_equipment:violation_trigger').toUpperCase()}</th>
                              <th>{t('store_equipment:event_severity').toUpperCase()}</th>
                              <th>{t('store_equipment:start_time').toUpperCase()}</th>
                              <th>{t('store_equipment:end_time').toUpperCase()}</th>
                              <th>{t('store_equipment:duration').toUpperCase()}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventRows.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.type}</td>
                                <td>{row.trigger || '—'}</td>
                                <td>{row.severity || '—'}</td>
                                <td>{row.startTime || '—'}</td>
                                <td>{row.endTime || '—'}</td>
                                <td>{row.duration || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </Box>
                  </div>
                  {renderPageFooter(1)}
                </div>

                {/* Pages 2..N — readings sheets, one paper per 3-column block */}
                {readingsPages.length === 0
                  ? null
                  : readingsPages.map((pageRows, pageIdx) => {
                      const cols = chunk(pageRows, READINGS_ROWS_PER_COLUMN);
                      while (cols.length < READINGS_COLUMNS_PER_PAGE) cols.push([]);
                      return (
                        <div key={`readings-${pageIdx}`} className="equipment-report-paper">
                          {renderPageHeader()}
                          <div className="equipment-report-paper-body">
                            <Box pb={3}>
                              <div className="equipment-report-section-bar">
                                {t('store_equipment:temperature_readings')} ({rawRows.length})
                                {pageIdx > 0 ? ` — ${t('store_equipment:continued')}` : ''}
                              </div>
                            </Box>
                            <div className={layoutClass}>
                              <div className="equipment-report-readings-page-grid">
                                {cols.map((colRows, colIdx) => (
                                  <div key={colIdx} className="equipment-report-readings-col">
                                    <div className="equipment-report-readings-col-header">
                                      <span className="col-time">{timeHeader}</span>
                                      <span className="col-temp">{tempHeader}</span>
                                      {hasDoor && <span className="col-door">{doorHeader}</span>}
                                    </div>
                                    {colRows.map((row, idx) => {
                                      const tempColor =
                                        row.tempBand === 'red'
                                          ? ThresholdColors.Red
                                          : row.tempBand === 'yellow'
                                            ? ThresholdColors.Yellow
                                            : null;
                                      return (
                                        <div key={idx} className="equipment-report-readings-col-row">
                                          <span className="col-time">{row.time}</span>
                                          <span className="col-temp">
                                            <span className="reading-dot-slot">
                                              {tempColor ? <span className="reading-dot" style={{ background: tempColor }} /> : null}
                                            </span>
                                            <span>{row.temp}</span>
                                          </span>
                                          {hasDoor && (
                                            <span className="col-door">
                                              <span className="reading-dot-slot">
                                                {row.doorOpen ? <span className="reading-dot" style={{ background: '#D946EF' }} /> : null}
                                              </span>
                                              <span>{row.door}</span>
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {renderPageFooter(pageIdx + 2)}
                        </div>
                      );
                    })}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
};
